/**
 * Formatting.
 *
 * `Intl.NumberFormat('en-IN')` everywhere (§16.2): ₹1,50,000, never ₹150,000.
 * A vendor reading a payout in the wrong grouping has to stop and count
 * digits, and the one place that must never happen is the amount they are
 * being paid.
 */
import type { Lang } from './i18n';

const MICROS = 1_000_000n;

export function formatINR(micros: string | bigint, decimals = 0): string {
  const value = typeof micros === 'bigint' ? micros : BigInt(micros || '0');
  const whole = value / MICROS;
  const fraction = value % MICROS;

  const asNumber = Number(whole) + Number(fraction) / 1_000_000;

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(asNumber);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-IN').format(value);
}

/** "आज", "कल", or a short date — a vendor thinks in days, not calendars. */
export function formatDueDate(date: string, lang: Lang): string {
  const days = daysUntil(date);

  if (lang === 'hi') {
    if (days === 0) return 'आज';
    if (days === 1) return 'कल';
    if (days < 0) return `${Math.abs(days)} दिन पहले`;
    if (days <= 7) return `${days} दिन में`;
  } else {
    if (days === 0) return 'today';
    if (days === 1) return 'tomorrow';
    if (days < 0) return `${Math.abs(days)} days ago`;
    if (days <= 7) return `in ${days} days`;
  }

  return new Date(`${date}T00:00:00`).toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN', {
    day: 'numeric',
    month: 'short',
  });
}

export function daysUntil(date: string): number {
  const target = Date.parse(`${date}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today.getTime()) / 86_400_000);
}

/**
 * Countdown to an RFQ closing.
 *
 * Deliberately coarse — "4 घंटे में" rather than a ticking clock. A live timer
 * on a job invitation manufactures panic, and the vendor is standing at a
 * press, not watching the screen.
 */
export function formatClosesIn(closesAt: string | null, lang: Lang): string | null {
  if (!closesAt) return null;

  const ms = Date.parse(closesAt) - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return lang === 'hi' ? 'बंद हो गया' : 'closed';

  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 1) {
    return lang === 'hi' ? `${hours} घंटे` : `${hours}h`;
  }

  const minutes = Math.max(1, Math.floor(ms / 60_000));
  return lang === 'hi' ? `${minutes} मिनट` : `${minutes}m`;
}

export function isClosingSoon(closesAt: string | null): boolean {
  if (!closesAt) return false;
  const ms = Date.parse(closesAt) - Date.now();
  return ms > 0 && ms < 4 * 3_600_000;
}

/** A whole due line: "Due tomorrow", "Due in 3 days", "7 days late", "Due 12 Nov". */
export function formatDue(date: string, lang: Lang): string {
  const days = daysUntil(date);
  if (lang === 'hi') {
    if (days === 0) return 'आज चाहिए';
    if (days === 1) return 'कल चाहिए';
    if (days < 0) return `${Math.abs(days)} दिन देर`;
    if (days <= 7) return `${days} दिन में चाहिए`;
    return `${shortDate(date, lang)} तक`;
  }
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days === -1) return '1 day late';
  if (days < 0) return `${Math.abs(days)} days late`;
  if (days <= 7) return `Due in ${days} days`;
  return `Due ${shortDate(date, lang)}`;
}

/** "12 Nov" (with the year only when it isn't this year). */
export function shortDate(date: string, lang: Lang): string {
  const d = new Date(date.length === 10 ? `${date}T00:00:00` : date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN', {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
  });
}

/** "1 Dec – 30 Dec", and how many days that is. */
export function formatRange(
  from: string,
  to: string,
  lang: Lang,
): { text: string; days: number } {
  const days =
    Math.round((Date.parse(`${to}T00:00:00`) - Date.parse(`${from}T00:00:00`)) / 86_400_000) +
    1;
  return { text: `${shortDate(from, lang)} – ${shortDate(to, lang)}`, days };
}

/** Whole rupees, en-IN grouping: ₹1,50,000. */
export const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;
